import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import { 
  CheckCircle2, 
  ArrowRight, 
  Home, 
  Clock,
  ExternalLink,
  Copy,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transferInfo, setTransferInfo] = useState(null);

  useEffect(() => {
    // Try to get transfer info from localStorage
    const pendingTransfers = JSON.parse(localStorage.getItem('pending_transfers') || '[]');
    if (pendingTransfers.length > 0) {
      // Get the most recent transfer
      const latestTransfer = pendingTransfers[pendingTransfers.length - 1];
      setTransferInfo(latestTransfer);
    }

    // Check for transaction ID in URL params
    const txId = searchParams.get('transaction_id') || searchParams.get('order_id');
    if (txId) {
      fetchTransactionStatus(txId);
    }
  }, [searchParams]);

  const fetchTransactionStatus = async (txId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/guardarian/order/${txId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setTransferInfo(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.error('Error fetching transaction:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-lg mx-auto">
          {/* Success Card */}
          <Card className="shadow-xl border-0 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2" />
            <CardHeader className="text-center pt-8">
              <div className="mx-auto w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl text-emerald-800">
                Payment Initiated!
              </CardTitle>
              <CardDescription className="text-base mt-2">
                Your transfer is being processed. The recipient will receive funds shortly.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pb-8">
              {/* Transfer Summary */}
              {transferInfo && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-emerald-600" />
                    Transfer Details
                  </h4>
                  
                  {transferInfo.order_id && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Order ID</span>
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-white px-2 py-1 rounded">{transferInfo.order_id}</code>
                        <button onClick={() => copyToClipboard(transferInfo.order_id)} className="text-gray-400 hover:text-gray-600">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {transferInfo.fiat_amount && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount Sent</span>
                      <span className="font-medium">{transferInfo.fiat_amount} {transferInfo.fiat_currency || 'USD'}</span>
                    </div>
                  )}
                  
                  {transferInfo.crypto_currency && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Via</span>
                      <span className="font-medium">{transferInfo.crypto_currency}</span>
                    </div>
                  )}
                  
                  {transferInfo.recipient_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Recipient</span>
                      <span className="font-medium">{transferInfo.recipient_name}</span>
                    </div>
                  )}
                  
                  {transferInfo.recipient_country && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Destination</span>
                      <span className="font-medium">{transferInfo.recipient_country}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Status Timeline */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-700">Transfer Status</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-700">Payment Received</p>
                      <p className="text-sm text-gray-500">Your payment has been processed</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-700">Converting to Crypto</p>
                      <p className="text-sm text-gray-500">Fiat converted to stablecoin</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center animate-pulse">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-amber-700">Processing Off-Ramp</p>
                      <p className="text-sm text-gray-500">Converting to recipient's currency...</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 opacity-50">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-500">Delivered</p>
                      <p className="text-sm text-gray-400">Funds sent to recipient's account</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estimated Time */}
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="font-medium text-blue-800">Estimated Delivery</p>
                <p className="text-blue-600">Within 5-30 minutes</p>
              </div>

              {/* Actions */}
              <div className="space-y-3 pt-4">
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  View Transaction History <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => navigate('/send-money')}
                  className="w-full"
                >
                  Send Another Transfer
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  <Home className="w-4 h-4 mr-2" /> Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Support Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Need help? Contact us at <a href="mailto:support@routeflowai.com" className="text-emerald-600 hover:underline">support@routeflowai.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}
